<template>
    <div class="w-full space-y-4">
        <h2 class="font-semibold text-xl text-center">{{ $t('views.reports.selectSitesPage.header') }}</h2>

        <div class="select-site-tabs">
            <Tabs teleport="#site-tabs-target" active-tab-class="--active">
                <nav class="flex justify-center overflow-x-auto mb-4">
                    <Tab class="mx-2" :name="$t('views.reports.selectSitesPage.farmsTabLabel')" :default="true" @click="onTabClicked(false)">
                        <SiteSelector :state="computedState" :selectable-sites="reportLocations" :batch-tab-is-active="false"></SiteSelector>
                    </Tab>
                    <Tab class="mx-2" :name="$t('views.reports.selectSitesPage.batchesTabLabel')" @click="onTabClicked(true)">
                        <SiteSelector :state="state" :selectable-sites="reportBatches" :batch-tab-is-active="true"></SiteSelector>
                    </Tab>
                </nav>
            </Tabs>
            <div id="site-tabs-target" class="flex justify-center h-full" />
        </div>

        <div class="flex justify-center">
            <CustomButton :disabled="disableNext || disableButtons" class="max-w-32" @click="onNextClicked">
                {{ $t('views.reports.nextButtonLabel') }}
            </CustomButton>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Tab, Tabs } from '@makeabledk/vue-ui/components/tabs';
import CustomButton from '@/components/ui/CustomButton.vue';
import SiteSelector from '@/views/dashboard/reports/selectSites/SiteSelector.vue';
import { Report, ReportBatch, ReportLocation, ReportViewState } from '@/types';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';

export default defineComponent({
    components: { CustomButton, Tab, Tabs, SiteSelector },
    props: {
        state: {
            type: Object as () => ReportViewState,
            required: true,
        },
    },
    emits: ['next', 'skip', 'update:state'],
    data() {
        return {
            batchTabIsActive: false,
            reportBatches: [] as ReportBatch[],
            reportLocations: [] as ReportLocation[],
            disableButtons: false,
        };
    },
    computed: {
        computedState: {
            get(): ReportViewState {
                return this.$props.state;
            },
            set(newValue: ReportViewState) {
                this.$emit('update:state', newValue);
            },
        },
        disableNext() {
            return this.computedState.siteIds.length < this.minNumberOfSelections || this.computedState.siteIds.length > this.maxNumberOfSelections;
        },
        minNumberOfSelections() {
            return 1;
        },
        maxNumberOfSelections() {
            return this.batchTabIsActive ? 4 : Number.MAX_SAFE_INTEGER;
        },
    },
    created() {
        store.dispatch(ActionType.GetReportBatches).then((batches: ReportBatch[]) => {
            this.reportBatches = batches;
        });
        store.dispatch(ActionType.GetReportLocations).then((locations: ReportLocation[]) => {
            this.reportLocations = locations;
        });
    },
    methods: {
        onTabClicked(isBatchTab: boolean) {
            this.batchTabIsActive = isBatchTab;
            Object.assign(this.computedState, { siteIds: [], selectedReportLocationKeys: [], selectedReportLocationBarnKeys: [] });
        },
        async onNextClicked() {
            if (this.disableNext || this.disableButtons) {
                return;
            }
            this.disableButtons = true;
            this.computedState.isBatchReport = this.batchTabIsActive;

            try {
                if (this.batchTabIsActive) {
                    const [reports, contentOptions] = await Promise.all([
                        store.dispatch(ActionType.GetReportData, {
                            form: { site_ids: this.computedState.siteIds, report_type: this.computedState.isBatchReport ? 'batch' : 'farm' },
                        }),
                        this.computedState.contentOptions.length ? this.computedState.contentOptions : store.dispatch(ActionType.GetReportContentOptions),
                    ]);
                    this.computedState.reports = reports;
                    this.computedState.contentOptions = contentOptions;
                    const applicableIds = [...new Set<number>(reports.flatMap((r: Report) => r.reconciliation_ids ?? []))];
                    this.computedState.applicableIds = applicableIds;
                    this.computedState.reconciliationIds = [...applicableIds];
                    this.$emit('skip');
                } else {
                    this.computedState.selectableDates = await store.dispatch(ActionType.GetReportDates, { form: { site_ids: this.computedState.siteIds, fetchBatchDates: this.batchTabIsActive } });
                    this.$emit('next');
                }
            } finally {
                this.disableButtons = false;
            }
        },
    },
});
</script>

<style scoped>
.select-site-tabs nav a {
    border-width: 1px;
    border-radius: 0.5rem;
    @apply border-opacity-20;
    padding: 0.5rem 1.25rem 0.5rem 1.25rem;
    font-weight: 600;
}

.select-site-tabs nav a.--active {
    @apply bg-primary-800;
    @apply border-primary-700;
    color: white;
}
</style>
